
import { defineComponent, ref, computed, onMounted, toRefs, watch } from "vue"
import { ElMessage, ElMessageBox } from "element-plus"
import type { ElForm } from "element-plus"
import ApiService from "@/core/services/ApiService"

type FormInstance = InstanceType<typeof ElForm>;

interface Product {
  id: number;
  name: string;
  image: string | null;
  recycle_price: number;
}

interface MatchPoolReward {
  id?: number;
  match_pool_id?: number;
  match_count: number;
  product_id: number;
  product?: Product;
  cost: number;
}

interface RewardForm {
  match_count: number;
  product_id: number | null;
  cost: number;
}

export default defineComponent({
  props: {
    matchPoolId: {
      type: Number,
      required: true
    }
  },
  emits: ['update'],
  setup(props, { emit }) {
    const { matchPoolId } = toRefs(props)

    const rewards = ref<MatchPoolReward[]>([])
    const dialogVisible = ref(false)
    const isEditingReward = ref(false)
    const editingRewardId = ref<number | null>(null)
    const rewardFormRef = ref<FormInstance>()

    const productOptions = ref<Product[]>([])
    const productSearchLoading = ref(false)

    const rewardForm = ref<RewardForm>({
      match_count: 0,
      product_id: null,
      cost: 0
    })

    const rewardRules = ref({
      match_count: [
        { required: true, message: '请输入碰数', trigger: 'blur' }
      ],
      product_id: [
        { required: true, message: '请选择奖励商品', trigger: 'change' }
      ],
      cost: [
        { required: true, message: '请输入成本价', trigger: 'blur' }
      ]
    })

    // 按碰数排序
    const sortedRewards = computed(() => {
      return [...rewards.value].sort((a, b) => a.match_count - b.match_count)
    })

    const getMatchCountTagType = (matchCount: number) => {
      if (matchCount === 0) return 'info'
      if (matchCount >= 9) return 'danger'
      if (matchCount >= 5) return 'warning'
      return 'success'
    }

    const loadRewards = async () => {
      try {
        ApiService.setHeader()
        const { data } = await ApiService.get(`admin/match_pools/${matchPoolId.value}`)
        rewards.value = data.match_pool?.match_pool_rewards || []
      } catch (error) {
        ElMessage.error('加载奖励列表失败')
      }
    }

    const searchProducts = async (query: string) => {
      if (!query || query.length < 1) {
        productOptions.value = []
        return
      }

      productSearchLoading.value = true
      try {
        ApiService.setHeader()
        const { data } = await ApiService.get(`admin/products?name=${encodeURIComponent(query)}&pageSize=20`)
        productOptions.value = data.products || []
      } catch (error) {
        ElMessage.error('搜索商品失败')
      } finally {
        productSearchLoading.value = false
      }
    }

    const onAddReward = () => {
      isEditingReward.value = false
      editingRewardId.value = null
      // 找出已配置的碰数，自动填充下一个
      const usedCounts = rewards.value.map(r => r.match_count)
      let nextCount = 0
      while (usedCounts.includes(nextCount)) {
        nextCount++
      }
      rewardForm.value = {
        match_count: nextCount,
        product_id: null,
        cost: 0
      }
      productOptions.value = []
      dialogVisible.value = true
    }

    const onEditReward = (row: MatchPoolReward) => {
      isEditingReward.value = true
      editingRewardId.value = row.id || null
      rewardForm.value = {
        match_count: row.match_count,
        product_id: row.product_id,
        cost: row.cost
      }
      // 预填充商品选项
      if (row.product) {
        productOptions.value = [row.product]
      }
      dialogVisible.value = true
    }

    const onSaveReward = async () => {
      if (!rewardFormRef.value) return

      try {
        await rewardFormRef.value.validate()
      } catch {
        return
      }

      try {
        ApiService.setHeader()

        const payload = {
          match_pool_reward: {
            match_count: rewardForm.value.match_count,
            product_id: rewardForm.value.product_id,
            cost: rewardForm.value.cost
          }
        }

        if (isEditingReward.value && editingRewardId.value) {
          // 更新
          await ApiService.put(
            `admin/match_pools/${matchPoolId.value}/match_pool_rewards/${editingRewardId.value}`,
            payload
          )
          ElMessage.success('更新成功')
        } else {
          // 创建
          await ApiService.post(
            `admin/match_pools/${matchPoolId.value}/match_pool_rewards`,
            payload
          )
          ElMessage.success('添加成功')
        }

        dialogVisible.value = false
        await loadRewards()
        emit('update')
      } catch (error: unknown) {
        const errorMsg = (error as { response?: { data?: { error_msg?: string } } })?.response?.data?.error_msg || '操作失败'
        ElMessage.error(errorMsg)
      }
    }

    const onDeleteReward = (row: MatchPoolReward) => {
      ElMessageBox.confirm(
        `确定要删除碰数 ${row.match_count} 的奖励配置吗？`,
        '删除确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
        .then(async () => {
          try {
            ApiService.setHeader()
            await ApiService.delete(
              `admin/match_pools/${matchPoolId.value}/match_pool_rewards/${row.id}`
            )
            ElMessage.success('删除成功')
            await loadRewards()
            emit('update')
          } catch (error) {
            ElMessage.error('删除失败')
          }
        })
        .catch(() => {
          // 取消删除
        })
    }

    onMounted(() => {
      loadRewards()
    })

    watch(matchPoolId, () => {
      loadRewards()
    })

    return {
      rewards,
      sortedRewards,
      dialogVisible,
      isEditingReward,
      rewardFormRef,
      rewardForm,
      rewardRules,
      productOptions,
      productSearchLoading,
      getMatchCountTagType,
      searchProducts,
      onAddReward,
      onEditReward,
      onSaveReward,
      onDeleteReward
    }
  }
})
